/* eslint-disable react-perf/jsx-no-new-array-as-prop */
import { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';

import { components } from 'app/marketplace/contentful/spaces/seo/entries/components';
import { noop } from 'app/shared/utils/utils';
import { breakpointShape } from 'app/shared/utils/propTypes';
import { ErrorPage } from 'app/shared/components/ErrorPage/ErrorPage';
import { ContentfulEntry } from 'app/marketplace/contentful/ContentfulEntry/ContentfulEntry';
import { ContentfulHeader } from 'app/marketplace/contentful/ContentfulHeader/ContentfulHeader';
import { ContentfulSt } from 'app/marketplace/contentful/ContentfulStylesSheet/ContentfulStylesSheet.css';
import {
  makeShape,
  modelShape,
  variantShape,
} from 'app/marketplace/contentful/spaces/seo/propTypes';
import {
  TopNavPropShape,
  TopNavDefaultProps,
} from 'app/shared/modules/pageTemplate/PageTemplate/PageTemplate';
import { Breadcrumb } from 'app/marketplace/search/Breadcrumb/Breadcrumb';
import { SectionSt } from 'app/marketplace/contentful/spaces/seo/components/SectionSt/SectionSt.css';
import { generateVariantPageSchema } from 'app/marketplace/contentful/spaces/seo/utils';
import { useResolvedLoading } from 'app/marketplace/contentful/spaces/seo/hooks/useResolvedLoading';

import { VariantSt } from './Variant.css';

export const Variant = ({
  make,
  variant,
  list,
  model,
  entry,
  locale,
  topNav,
  getPage,
  location,
  breakpoint,
  getMakePage,
  getModelPage,
  getVehicles,
  vehicleCount,
  priceRange,
  favouriteIds,
  performanceUnit,
  toggleFavourite,
  budgetRangeValue,
  updateFilters,
  isLoading,
}) => {
  const { pathname } = location;
  const { hasResolvedLoading } = useResolvedLoading({ isLoading });

  /* istanbul ignore next */
  useEffect(() => {
    topNav.setTarget(pathname);
    if (!variant) getPage();
    if (!model) getModelPage();
    if (!make) getMakePage();
  }, [pathname]);

  /* istanbul ignore next */
  useEffect(() => {
    updateFilters(make, model, variant);
    getVehicles();
  }, [make, model, variant]);

  const breadcrumbLinks = useMemo(
    () => [
      { to: '/auto', text: 'Auto' },
      { to: `/auto/${make?.path}`, text: make?.title },
      { to: `/auto/${make?.path}/${model?.path}`, text: model?.title },
      {
        to: `/auto/${make?.path}/${model?.path}/${variant?.path}`,
        text: variant?.title,
      },
    ],
    [make, model, variant],
  );

  if ((entry && entry.error) || !make || !model) {
    return (
      <ErrorPage
        errorCode={404}
        errorId="marketplace.contentful.ContentfulEntry.notFound"
      />
    );
  }

  const htmlIndex = entry?.fields?.htmlIndex || 'noindex,nofollow';

  return (
    <ContentfulSt>
      <ContentfulHeader
        entry={entry}
        location={location}
        fallbackHtmlIndex={htmlIndex}
        showDefaultJsonLd={false}
        jsonLd={
          entry && hasResolvedLoading
            ? [
                generateVariantPageSchema({
                  entry,
                  vehicleCount,
                  location,
                  list,
                  make,
                  breadcrumbLinks,
                  currentModel: model,
                  priceRange,
                }),
              ]
            : []
        }
      />
      <VariantSt>
        <SectionSt order={0} mb={[16, null, 20]}>
          <Breadcrumb additionalLinks={breadcrumbLinks} />
        </SectionSt>
        {entry && (
          <ContentfulEntry
            make={make}
            variant={variant}
            list={list}
            model={model}
            entry={entry}
            locale={locale}
            isLoading={!hasResolvedLoading}
            components={components}
            breakpoint={breakpoint}
            vehicleCount={vehicleCount}
            favouriteIds={favouriteIds}
            toggleFavourite={toggleFavourite}
            performanceUnit={performanceUnit}
            budgetRangeValue={budgetRangeValue}
            priceRange={priceRange}
          />
        )}
      </VariantSt>
    </ContentfulSt>
  );
};

Variant.propTypes = {
  make: makeShape,
  model: modelShape,
  variant: variantShape,
  entry: PropTypes.object,
  getPage: PropTypes.func,
  topNav: TopNavPropShape,
  locale: PropTypes.string,
  location: PropTypes.object,
  getMakePage: PropTypes.func,
  getModelPage: PropTypes.func,
  updateFilters: PropTypes.func,
  breakpoint: breakpointShape,
  isLoading: PropTypes.bool,
  getVehicles: PropTypes.func,
  list: PropTypes.array,
  vehicleCount: PropTypes.number,
  priceRange: PropTypes.object,
  favouriteIds: PropTypes.array,
  toggleFavourite: PropTypes.func.isRequired,
  performanceUnit: PropTypes.string.isRequired,
  budgetRangeValue: PropTypes.string.isRequired,
};

Variant.defaultProps = {
  make: null,
  model: null,
  variant: null,
  entry: null,
  locale: 'de',
  location: {},
  getPage: noop,
  getMakePage: noop,
  getModelPage: noop,
  topNav: TopNavDefaultProps,
  breakpoint: 'sm',
  getVehicles: noop,
  updateFilters: noop,
  list: [],
  isLoading: false,
  vehicleCount: 0,
  priceRange: { min: 0, max: 0 },
  favouriteIds: [],
};
